import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { TextField, MenuItem } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import CustomerProfileService from './CustomerProfileService';
import { useAuth } from '../../context/AuthContext';

// changedAt is stored in UTC — always render it in Bangkok time (ICT) so the log
// reads correctly no matter where the admin's machine is set.
const DATE_OPTS = { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'Asia/Bangkok' };
const TIME_OPTS = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok' };
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', DATE_OPTS) : '');
const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString('en-GB', TIME_OPTS).replace(/^24:/, '00:') : '');

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.75)',
    zIndex: 1300,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'min(1100px, 96vw)',
    maxHeight: 'min(85vh, 800px)',
    borderRadius: '12px',
    overflow: 'auto',
  },
};

const CustomerChangeLogModal = ({ isOpen, onClose, profile }) => {
  const [rowData, setRowData] = useState([]);
  const [fieldFilter, setFieldFilter] = useState(''); // '' = show all fields
  const { authState } = useAuth();
  const isSuperuser = authState?.user?.role === 'superuser';

  useEffect(() => {
    const fetchLogs = async () => {
      if (!isOpen || !profile?.id) return;
      try {
        const response = await CustomerProfileService.getChangeLogs('customer_profile', profile.id);
        const result = response.data || [];
        const formattedData = result.map((log, index) => ({
          no: index + 1,
          changedAt: log.changedAt,
          field: log.fieldName,
          old: log.oldValue,
          new: log.newValue,
          user: log.User ? `${log.User.nickname}` : `System`, // 'System' when changedBy is 999
          source: log.source || '',
        }));
        setRowData(formattedData);
      } catch (err) {
        console.log(err);
        setRowData([]);
      }
    };
    fetchLogs();
  }, [isOpen, profile]);

  const fieldOptions = [...new Set(rowData.map((r) => r.field).filter(Boolean))].sort();
  const displayedRows = fieldFilter ? rowData.filter((r) => r.field === fieldFilter) : rowData;

  const columns = [
    { headerName: "No", field: "no", flex: 1 },
    // Date + Time both bind to the raw changedAt so sorting either is chronological
    { headerName: "Date", colId: "date", field: "changedAt", flex: 1, sort: 'desc', valueFormatter: (p) => fmtDate(p.value) },
    { headerName: "Time", colId: "time", field: "changedAt", flex: 1, valueFormatter: (p) => fmtTime(p.value) },
    { headerName: "Field", field: "field", flex: 1 },
    { headerName: "Old", field: "old", flex: 1 },
    { headerName: "New", field: "new", flex: 1 },
    { headerName: "User", field: "user", flex: 1 },
    ...(isSuperuser ? [{ headerName: "Source", field: "source", flex: 1 }] : []),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      ariaHideApp={false}
      contentLabel="ประวัติการแก้ไขโปรไฟล์"
    >
      <div className='flex justify-between items-start'>
        <div>
          <h2 className='text-4xl'>ประวัติการแก้ไขโปรไฟล์</h2>
          <div className='mt-3'>
            <TextField label="Contact" variant="outlined" value={profile?.contact || ''} disabled />
            <TextField sx={{ ml: 2 }} label="ชื่อ Smile" variant="outlined" value={profile?.smileName || ''} disabled />
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-sm px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white"
        >
          ปิด
        </button>
      </div>
      <div className='mt-3'>
        <TextField
          select
          size="small"
          label="กรองตาม Field"
          value={fieldFilter}
          onChange={(e) => setFieldFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">ทั้งหมด</MenuItem>
          {fieldOptions.map((f) => (
            <MenuItem key={f} value={f}>{f}</MenuItem>
          ))}
        </TextField>
      </div>
      <div className="ag-theme-alpine mt-3" style={{ width: '100%' }}>
        <AgGridReact
          rowData={displayedRows}
          columnDefs={columns}
          domLayout='autoHeight'
          defaultColDef={{ resizable: true, sortable: true }}
        />
      </div>
    </Modal>
  );
};

export default CustomerChangeLogModal;
